import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './i18n'; // Import i18n configuration
import App from './App';
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import ContactPage from './pages/ContactPage';
import AboutPage from './pages/AboutPage';
import MissionPage from './pages/MissionPage';
import HistoriquePage from './pages/HistoriquePage';
import EquipePage from './pages/EquipePage';
import DonPage from './pages/DonPage';
import AHBEducationPage from './pages/AHBEducationPage';

import CarrierePage from './pages/CarrierePage';
import AdhesionPage from './pages/AdhesionPage';
import DocumentPage from './pages/DocumentPage';
import ActualitePage from './pages/ActualitePage';
import ProjetPage from './pages/ProjetPage';
import PublicationPage from './pages/PublicationPage';
import DeveloppementPage from './pages/DeveloppementPage';
import SanteSecuritePage from './pages/SanteSecuritePage';
import EauAssainissementPage from './pages/EauAssainissementPage';
import EnvironnementPage from './pages/EnvironnementPage';
import SlideDetail from './pages/SlideDetail';
import FullDescriptionPage from './pages/FullDescriptionPage';
import ProjectDetail from './pages/ProjectDetail';
import ActualiteDetail from './pages/ActualiteDetail';
import NewDetailsPage from './pages/NewDetailsPage';
import InfoDetail from './pages/InfoDetail';
import PublicationDetail from './pages/PublicationDetail';
import NotFoundPage from './pages/NotFoundPage';
import i18n from './i18n';
import WhatsAppFloat from './components/WhatsAppFloat';
import SocialFloat from './components/SocialFloat';
import ChatBot from './components/ChatBot';
import SEOHead from './components/SEOHead';

import usePageTitle from './hooks/usePageTitle';

// Composant pour scroller vers le haut à chaque changement de page
const ScrollToTop = () => {
  const location = useLocation();

  // Gestion automatique du titre de page + pageview GA
  usePageTitle();

  useEffect(() => {
    // Ne pas scroller si c'est un changement de langue (preserveScroll dans state)
    if (location.state?.preserveScroll) {
      return;
    }
    
    // Scroll instantané vers le haut pour une navigation rapide
    window.scrollTo(0, 0);
  }, [location.pathname, location.state]);

  return null;
};

// Composant simple pour gérer la synchronisation de langue
const LanguageManager = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const pathname = location.pathname;
    
    // Détecter la langue depuis l'URL
    const detectLanguageFromURL = () => {
      if (pathname.startsWith('/en/')) {
        return 'en';
      } else if (pathname.startsWith('/fr/')) {
        return 'fr';
      }
      return null;
    };

    const urlLang = detectLanguageFromURL();
    const currentLang = i18n.language || 'fr';

    // Gérer la racine
    if (pathname === '/') {
      navigate('/fr', { replace: true });
      return;
    }

    // Synchroniser la langue si nécessaire (sans redirection)
    if (urlLang && urlLang !== currentLang) {
      console.log(`🔄 Synchronisation: URL=${urlLang}, i18n=${currentLang}`);
      i18n.changeLanguage(urlLang);
      localStorage.setItem('i18nextLng', urlLang);
      
      // Notifier les composants
      window.dispatchEvent(new CustomEvent('languageChanged'));
    }
  }, [location.pathname, navigate]);

  return children;
};

const AppRouter = () => {
  return (
    <BrowserRouter>
      <LanguageManager>
        <ScrollToTop />
        <SEOHead />
        <Routes>
          {/* Routes racine avec redirection automatique */}
          <Route path="/" element={<Navigate to="/fr" replace />} />
          
          {/* Routes localisées */}
          <Route path="/fr" element={<App />} />
          <Route path="/en" element={<App />} />
          
          {/* Routes avec paramètres de langue - FR */}
          <Route path="/fr/contact" element={<ContactPage />} />
          <Route path="/fr/about" element={<AboutPage />} />
          <Route path="/fr/mission" element={<MissionPage />} />
          <Route path="/fr/historique" element={<HistoriquePage />} />
          <Route path="/fr/equipe" element={<EquipePage />} />
          <Route path="/fr/don" element={<DonPage />} />

          <Route path="/fr/carriere" element={<CarrierePage />} />
          <Route path="/fr/adhesion" element={<AdhesionPage />} />
          <Route path="/fr/documents" element={<DocumentPage />} />
          <Route path="/fr/actualites" element={<ActualitePage />} />
          <Route path="/fr/projets" element={<ProjetPage />} />
          <Route path="/fr/developpement" element={<DeveloppementPage />} />
          <Route path="/fr/projet/sante-et-securite" element={<SanteSecuritePage />} />
          <Route path="/fr/projet/eau-et-assainissement" element={<EauAssainissementPage />} />
          <Route path="/fr/projet/environnement" element={<EnvironnementPage />} />
          <Route path="/fr/projet/ahb-education" element={<AHBEducationPage />} />
          <Route path="/fr/publications" element={<PublicationPage />} />
          <Route path="/fr/full-description/:slug" element={<FullDescriptionPage />} />
          <Route path="/fr/slide/:slug" element={<SlideDetail />} />
          <Route path="/fr/projet/:slug" element={<ProjectDetail />} />
          <Route path="/fr/actualite/:slug" element={<ActualiteDetail />} />
          <Route path="/fr/news/:id" element={<NewDetailsPage />} />
          <Route path="/fr/info/:slug" element={<InfoDetail />} />
          <Route path="/fr/publication/:slug" element={<PublicationDetail />} />
          
          {/* Routes avec paramètres de langue - EN */}
          <Route path="/en/contact" element={<ContactPage />} />
          <Route path="/en/about" element={<AboutPage />} />
          <Route path="/en/mission" element={<MissionPage />} />
          <Route path="/en/historique" element={<HistoriquePage />} />
          <Route path="/en/equipe" element={<EquipePage />} />
          <Route path="/en/don" element={<DonPage />} />

          <Route path="/en/carriere" element={<CarrierePage />} />
          <Route path="/en/adhesion" element={<AdhesionPage />} />
          <Route path="/en/documents" element={<DocumentPage />} />
          <Route path="/en/actualites" element={<ActualitePage />} />
          <Route path="/en/projets" element={<ProjetPage />} />
          <Route path="/en/developpement" element={<DeveloppementPage />} />
          <Route path="/en/projet/sante-et-securite" element={<SanteSecuritePage />} />
          <Route path="/en/projet/eau-et-assainissement" element={<EauAssainissementPage />} />
          <Route path="/en/projet/environnement" element={<EnvironnementPage />} />
          <Route path="/en/projet/ahb-education" element={<AHBEducationPage />} />
          <Route path="/en/publications" element={<PublicationPage />} />
          <Route path="/en/full-description/:slug" element={<FullDescriptionPage />} />
          <Route path="/en/slide/:slug" element={<SlideDetail />} />
          <Route path="/en/projet/:slug" element={<ProjectDetail />} />
          <Route path="/en/actualite/:slug" element={<ActualiteDetail />} />
          <Route path="/en/news/:id" element={<NewDetailsPage />} />
          <Route path="/en/info/:slug" element={<InfoDetail />} />
          <Route path="/en/publication/:slug" element={<PublicationDetail />} />
          
          {/* Route catchall pour la page 404 - doit être la dernière */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>

        {/* WhatsApp Floating Button - visible on all pages */}
        <WhatsAppFloat />

        {/* Social Media Sidebar - BAD style, fixed left */}
        <SocialFloat />

        {/* ChatBot - fixed bottom left */}
        <ChatBot />
      </LanguageManager>
    </BrowserRouter>
  );
};

// Attendre que i18n soit initialisé avant de rendre l'application
const renderApp = () => {
  ReactDOM.createRoot(document.getElementById('root')).render(
    <AppRouter />
  );
};

// Vérifier si i18n est prêt, sinon attendre
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', renderApp);
} else {
  renderApp();
}

